import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';

import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil, debounceTime } from 'rxjs';
import { DateTime } from 'luxon';

import { 
    DocumentsFilterService, 
    DocumentFilters, 
    DocumentStats,
    ViewTab,
    PaymentStatusFilter,
    SortField,
    SortDirection,
    AnnotationFilterKey
} from 'shared/services/documents-filter.service';

interface ViewTabConfig {
    id: ViewTab;
    label: string;
    icon: string;
}

interface AnnotationConfig {
    id: AnnotationFilterKey;
    label: string;
    icon: string;
    color: string;
}

interface PaymentStatusConfig {
    id: PaymentStatusFilter;
    label: string;
    icon: string;
    color: string;
}

interface KsefStatusConfig {
    id: 'all' | 'synced' | 'sending' | 'pending' | 'expired' | 'error' | 'not-required';
    label: string;
    icon: string;
    color: string;
}

interface PaymentMethodConfig {
    ksefValue: number;
    label: string;
    icon: string;
}

interface SortOptionConfig {
    field: SortField;
    direction: SortDirection;
    label: string;
    icon: string;
}

interface DocumentTypeConfig {
    id: string;
    label: string;
}

// Klucz localStorage do przechowywania stanu zwiniętych/rozwiniętych sekcji
const COLLAPSED_SECTIONS_KEY = 'fakturnia_filters_collapsed_sections';

// Sekcje, które można zwijać
type CollapsibleSection = 'ksefStatus' | 'paymentStatus' | 'paymentMethods' | 'documentTypes' | 'annotations';

@Component({
    selector: 'app-documents-filters-panel',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatBadgeModule,
        MatTooltipModule
    ],
    templateUrl: './documents-filters-panel.component.html',
    styleUrls: ['./documents-filters-panel.component.scss']
})
export class DocumentsFiltersPanelComponent implements OnInit, OnDestroy {
    
    private _destroy$ = new Subject<void>();
    private _searchSubject$ = new Subject<string>();
    
    @Input() isMobile = false;
    @Output() closePanel = new EventEmitter<void>();
    
    filters: DocumentFilters;
    stats: DocumentStats;
    
    // Stan zwiniętych sekcji (true = zwinięta)
    collapsedSections: Record<CollapsibleSection, boolean> = {
        ksefStatus: false,
        paymentStatus: false,
        paymentMethods: true,
        documentTypes: true,
        annotations: true
    };
    
    // Konfiguracja tabów widoku
    viewTabs: ViewTabConfig[] = [
        { id: 'all', label: 'Wszystkie', icon: 'tabler_outline:files' },
        { id: 'drafts', label: 'Szkice', icon: 'tabler_outline:file-pencil' },
        { id: 'sales', label: 'Sprzedaż', icon: 'tabler_outline:arrow-up-right' },
        { id: 'purchases', label: 'Zakupy', icon: 'tabler_outline:arrow-down-left' },
        { id: 'overdue', label: 'Po terminie', icon: 'tabler_outline:alert-circle' },
        // TODO: Odkomentuj gdy będą gotowe dokumenty magazynowe
        // { id: 'warehouse', label: 'Magazyn', icon: 'tabler_outline:package' },
        // { id: 'other', label: 'Inne', icon: 'tabler_outline:dots' }
    ];
    
    // Konfiguracja źródeł
    sources: { id: 'all' | 'local' | 'ksef', label: string }[] = [
        { id: 'all', label: 'Wszystkie' },
        { id: 'local', label: 'Lokalne' },
        { id: 'ksef', label: 'KSeF' }
    ];
    
    // Konfiguracja statusów KSeF
    ksefStatuses: KsefStatusConfig[] = [
        { id: 'all', label: 'Wszystkie', icon: 'tabler_outline:list', color: '' },
        { id: 'synced', label: 'W KSeF', icon: 'tabler_outline:circle-check', color: 'text-green-500' },
        { id: 'sending', label: 'W trakcie wysłania', icon: 'tabler_outline:loader-2', color: 'text-blue-500' },
        { id: 'pending', label: 'Oczekujące', icon: 'tabler_outline:clock', color: 'text-orange-500' },
        { id: 'error', label: 'Błąd wysyłki', icon: 'tabler_outline:alert-circle', color: 'text-red-500' },
        { id: 'expired', label: 'Nie wysłano', icon: 'tabler_outline:alert-triangle', color: 'text-amber-500' },
        { id: 'not-required', label: 'Nie dotyczy', icon: 'tabler_outline:circle-off', color: 'text-gray-400' }
    ];
    
    // Konfiguracja statusów płatności
    paymentStatuses: PaymentStatusConfig[] = [
        { id: 'all', label: 'Wszystkie', icon: 'tabler_outline:list', color: '' },
        { id: 'overdue', label: 'Po terminie', icon: 'tabler_outline:alert-circle', color: 'text-red-500' },
        { id: 'received', label: 'Zapłacone', icon: 'tabler_outline:circle-check', color: 'text-green-500' },
        { id: 'undefined', label: 'Nie określono', icon: 'tabler_outline:help', color: 'text-gray-400' }
    ];
    
    // Konfiguracja metod płatności (grupowane po ksefValue z PaymentMethodsSimplified)
    paymentMethodOptions: PaymentMethodConfig[] = [
        { ksefValue: 6, label: 'Przelew', icon: 'tabler_outline:building-bank' },
        { ksefValue: 1, label: 'Gotówka', icon: 'tabler_outline:cash' },
        { ksefValue: 2, label: 'Karta', icon: 'tabler_outline:credit-card' },
        { ksefValue: 3, label: 'Bon', icon: 'tabler_outline:ticket' },
        { ksefValue: 4, label: 'Czek', icon: 'tabler_outline:file-certificate' },
        { ksefValue: 5, label: 'Kredyt', icon: 'tabler_outline:coins' },
        { ksefValue: 7, label: 'Płatność mobilna', icon: 'tabler_outline:device-mobile' },
    ];
    
    // Opcje sortowania
    sortOptions: SortOptionConfig[] = [
        { field: 'date', direction: 'desc', label: 'Najnowsze', icon: 'tabler_outline:sort-descending' },
        { field: 'date', direction: 'asc', label: 'Najstarsze', icon: 'tabler_outline:sort-ascending' },
        { field: 'amount', direction: 'desc', label: 'Kwota ↓', icon: 'tabler_outline:sort-descending-numbers' },
        { field: 'amount', direction: 'asc', label: 'Kwota ↑', icon: 'tabler_outline:sort-ascending-numbers' }
    ];
    
    // Dostępne typy dokumentów (id musi odpowiadać templateId z fakturnia-shared/templates)
    documentTypes: DocumentTypeConfig[] = [
        { id: 'FakturaVAT', label: 'Faktura VAT' },
        { id: 'Faktura', label: 'Faktura (bez VAT)' },
        { id: 'FakturaKorygujaca', label: 'Faktura korygująca' },
        { id: 'FakturaKorygujacaZaliczkowa', label: 'Korekta faktury zaliczkowej' },
        { id: 'FakturaKorygujacaRozliczeniowa', label: 'Korekta faktury rozliczeniowej' },
        { id: 'FakturaZaliczka', label: 'Faktura zaliczkowa' },
        { id: 'FakturaRozliczeniowa', label: 'Faktura rozliczeniowa' },
        { id: 'FakturaProforma', label: 'Proforma' },
        { id: 'FakturaProformaBezVAT', label: 'Proforma (bez VAT)' },
        { id: 'Zamowienie', label: 'Zamówienie' }
    ];
    
    // Konfiguracja filtrów adnotacji
    annotationFilters: AnnotationConfig[] = [
        { id: 'isSelfInvoicing', label: 'Samofakturowanie', icon: 'tabler_outline:replace', color: 'text-amber-500' },
        { id: 'isSplitPayment', label: 'Mechanizm podzielonej płatności (MPP)', icon: 'tabler_outline:cash', color: 'text-blue-500' },
        { id: 'isReverseCharge', label: 'Odwrotne obciążenie', icon: 'tabler_outline:arrows-exchange', color: 'text-purple-500' },
        { id: 'isCashMethod', label: 'Metoda kasowa', icon: 'tabler_outline:building-bank', color: 'text-green-500' },
        { id: 'hasMarginProcedure', label: 'Procedura marży', icon: 'tabler_outline:percentage', color: 'text-orange-500' },
        { id: 'hasVatExemption', label: 'Zwolnienie z VAT', icon: 'tabler_outline:receipt-off', color: 'text-gray-500' }
    ];
    
    searchValue = '';
    
    // Aktywny preset daty (obliczany przy zmianie filtrów)
    activePreset: 'today' | 'last7days' | 'thisMonth' | 'last30days' | 'lastMonth' | null = null;
    
    // Gettery do konwersji ISO string na DateTime dla mat-datepicker
    get dateFromValue(): DateTime | null {
        return this.filters.dateFrom ? DateTime.fromISO(this.filters.dateFrom) : null;
    }
    
    get dateToValue(): DateTime | null {
        return this.filters.dateTo ? DateTime.fromISO(this.filters.dateTo) : null;
    }
    
    // Formatowane daty do wyświetlenia
    get dateFromDisplay(): string {
        return this.filters.dateFrom ? DateTime.fromISO(this.filters.dateFrom).toFormat('dd.MM.yyyy') : '';
    }
    
    get dateToDisplay(): string {
        return this.filters.dateTo ? DateTime.fromISO(this.filters.dateTo).toFormat('dd.MM.yyyy') : '';
    }
    
    constructor(public filterService: DocumentsFilterService) {
        this.filters = this.filterService.currentFilters;
        this.stats = this.filterService.currentStats;
        this._loadCollapsedSections();
    }
    
    ngOnInit(): void {
        // Subskrybuj zmiany filtrów
        this.filterService.filters$
            .pipe(takeUntil(this._destroy$))
            .subscribe(filters => {
                this.filters = filters;
                this.searchValue = filters.search || '';
                this.activePreset = this._calculateActivePreset();
            });
        
        // Subskrybuj statystyki
        this.filterService.stats$
            .pipe(takeUntil(this._destroy$))
            .subscribe(stats => {
                this.stats = stats;
            });
        
        // Debounce wyszukiwania
        this._searchSubject$
            .pipe(
                takeUntil(this._destroy$),
                debounceTime(300)
            )
            .subscribe(search => {
                this.filterService.updateFilters({ search: search || undefined });
            });
    }
    
    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }
    
    setView(view: ViewTab): void {
        // Bazowe filtry - resetuj wszystkie przy zmianie widoku
        const baseFilters = {
            view,
            source: 'all' as const,
            ksefStatus: 'all' as const,
            paymentStatus: 'all' as const,
            // Zachowaj wyszukiwanie i daty - te są niezależne od widoku
            // documentTypes i paymentMethods też resetujemy
            documentTypes: undefined as string[] | undefined,
            paymentMethods: [] as number[]
        };
        
        if (view === 'drafts') {
            // Szkice: tylko lokalne dokumenty
            this.filterService.updateFilters({ 
                ...baseFilters,
                source: 'local'
            });
        } else if (view === 'overdue') {
            // Po terminie: filtr płatności na overdue
            this.filterService.updateFilters({ 
                ...baseFilters,
                paymentStatus: 'overdue'
            });
        } else {
            // Wszystkie, Sprzedaż, Zakupy - czyste filtry
            this.filterService.updateFilters(baseFilters);
        }
    }
    
    setSource(source: 'all' | 'local' | 'ksef'): void {
        this.filterService.updateFilters({ source });
    }
    
    setKsefStatus(status: 'all' | 'synced' | 'sending' | 'pending' | 'expired' | 'error' | 'not-required'): void {
        this.filterService.updateFilters({ ksefStatus: status });
    }
    
    setPaymentStatus(status: PaymentStatusFilter): void {
        this.filterService.updateFilters({ paymentStatus: status });
    }
    
    setSort(field: SortField, direction: SortDirection): void {
        this.filterService.updateFilters({ sort: { field, direction } });
    }
    
    onSearchInput(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.searchValue = value;
        this._searchSubject$.next(value);
    }
    
    clearSearch(): void {
        this.searchValue = '';
        this.filterService.updateFilters({ search: undefined });
    }
    
    onDateFromChange(date: any): void {
        if (!date) {
            this.filterService.updateFilters({ dateFrom: undefined });
            return;
        }
        // Luxon DateTime - użyj toISODate() dla formatu YYYY-MM-DD
        const isoDate = date.toISODate ? date.toISODate() : DateTime.fromJSDate(date).toISODate();
        this.filterService.updateFilters({ 
            dateFrom: isoDate || undefined 
        });
    }
    
    onDateToChange(date: any): void {
        if (!date) {
            this.filterService.updateFilters({ dateTo: undefined });
            return;
        }
        // Luxon DateTime - użyj toISODate() dla formatu YYYY-MM-DD
        const isoDate = date.toISODate ? date.toISODate() : DateTime.fromJSDate(date).toISODate();
        this.filterService.updateFilters({ 
            dateTo: isoDate || undefined 
        });
    }
    
    clearDateFrom(): void {
        this.filterService.updateFilters({ dateFrom: undefined });
    }
    
    clearDateTo(): void {
        this.filterService.updateFilters({ dateTo: undefined });
    }
    
    /**
     * Predefiniowane zakresy dat
     */
    setDatePreset(preset: 'today' | 'last7days' | 'thisMonth' | 'last30days' | 'lastMonth'): void {
        // Jeśli ten preset jest już aktywny, zresetuj daty (toggle)
        if (this.activePreset === preset) {
            this.filterService.updateFilters({ dateFrom: undefined, dateTo: undefined });
            return;
        }
        
        const today = DateTime.now();
        let dateFrom: string;
        let dateTo: string;
        
        switch (preset) {
            case 'today':
                dateFrom = today.toISODate()!;
                dateTo = today.toISODate()!;
                break;
            case 'last7days':
                dateFrom = today.minus({ days: 7 }).toISODate()!;
                dateTo = today.toISODate()!;
                break;
            case 'thisMonth':
                dateFrom = today.startOf('month').toISODate()!;
                dateTo = today.endOf('month').toISODate()!;
                break;
            case 'last30days':
                dateFrom = today.minus({ days: 30 }).toISODate()!;
                dateTo = today.toISODate()!;
                break;
            case 'lastMonth':
                const lastMonth = today.minus({ months: 1 });
                dateFrom = lastMonth.startOf('month').toISODate()!;
                dateTo = lastMonth.endOf('month').toISODate()!;
                break;
        }
        
        this.filterService.updateFilters({ dateFrom, dateTo });
    }
    
    /**
     * Oblicza aktywny preset na podstawie aktualnych dat filtrów
     */
    private _calculateActivePreset(): 'today' | 'last7days' | 'thisMonth' | 'last30days' | 'lastMonth' | null {
        const { dateFrom, dateTo } = this.filters;
        if (!dateFrom || !dateTo) return null;
        
        const today = DateTime.now();
        const todayStr = today.toISODate()!;
        
        // Sprawdź 'today'
        if (dateFrom === todayStr && dateTo === todayStr) {
            return 'today';
        }
        
        // Sprawdź 'last7days'
        if (dateFrom === today.minus({ days: 7 }).toISODate()! && 
            dateTo === todayStr) {
            return 'last7days';
        }
        
        // Sprawdź 'thisMonth'
        if (dateFrom === today.startOf('month').toISODate()! && 
            dateTo === today.endOf('month').toISODate()!) {
            return 'thisMonth';
        }
        
        // Sprawdź 'last30days'
        if (dateFrom === today.minus({ days: 30 }).toISODate()! && 
            dateTo === todayStr) {
            return 'last30days';
        }
        
        // Sprawdź 'lastMonth'
        const lastMonth = today.minus({ months: 1 });
        if (dateFrom === lastMonth.startOf('month').toISODate()! && 
            dateTo === lastMonth.endOf('month').toISODate()!) {
            return 'lastMonth';
        }
        
        return null;
    }
    
    toggleDocumentType(typeId: string): void {
        const current = this.filters.documentTypes || [];
        const updated = current.includes(typeId)
            ? current.filter(t => t !== typeId)
            : [...current, typeId];
        this.filterService.updateFilters({ documentTypes: updated });
    }
    
    isDocumentTypeSelected(typeId: string): boolean {
        return this.filters.documentTypes?.includes(typeId) || false;
    }
    
    // === Metody do obsługi filtrów metod płatności ===
    togglePaymentMethod(ksefValue: number): void {
        const current = this.filters.paymentMethods || [];
        const updated = current.includes(ksefValue)
            ? current.filter(v => v !== ksefValue)
            : [...current, ksefValue];
        this.filterService.updateFilters({ paymentMethods: updated });
    }
    
    isPaymentMethodSelected(ksefValue: number): boolean {
        return this.filters.paymentMethods?.includes(ksefValue) || false;
    }
    
    // === Metody do obsługi filtrów adnotacji ===
    toggleAnnotation(annotationId: AnnotationFilterKey): void {
        const current = this.filters.annotations || [];
        const updated = current.includes(annotationId)
            ? current.filter(a => a !== annotationId)
            : [...current, annotationId];
        this.filterService.updateFilters({ annotations: updated });
    }
    
    isAnnotationSelected(annotationId: AnnotationFilterKey): boolean {
        return this.filters.annotations?.includes(annotationId) || false;
    }
    
    // === Zwijanie/rozwijanie sekcji filtrów ===
    toggleSection(section: CollapsibleSection): void {
        this.collapsedSections[section] = !this.collapsedSections[section];
        this._saveCollapsedSections();
    }
    
    isSectionCollapsed(section: CollapsibleSection): boolean {
        return this.collapsedSections[section];
    }
    
    private _loadCollapsedSections(): void {
        try {
            const saved = localStorage.getItem(COLLAPSED_SECTIONS_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                // Scal z domyślnymi wartościami (na wypadek nowych sekcji)
                this.collapsedSections = { ...this.collapsedSections, ...parsed };
            }
        } catch (e) {
            // Ignoruj błędy parsowania
        }
    }
    
    private _saveCollapsedSections(): void {
        try {
            localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(this.collapsedSections));
        } catch (e) {
            // Ignoruj błędy zapisu
        }
    }
    
    clearFilters(): void {
        this.filterService.resetFiltersKeepView();
        this.searchValue = '';
    }
    
    get hasActiveFilters(): boolean {
        return this.filterService.hasActiveFilters();
    }
    
    get activeFiltersCount(): number {
        return this.filterService.getActiveFiltersCount();
    }
    
    getStatForView(viewId: ViewTab): number {
        switch (viewId) {
            case 'all': return this.stats.all;
            case 'drafts': return this.stats.drafts;
            case 'sales': return this.stats.sales;
            case 'purchases': return this.stats.purchases;
            case 'warehouse': return this.stats.warehouse;
            case 'other': return this.stats.other;
            case 'overdue': return this.stats.overdue;
            default: return 0;
        }
    }
}
